import React from 'react';
import { BlurView } from 'expo-blur';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { View, StyleSheet, TouchableOpacity } from 'react-native';

import { pointerEventsStyles, BodyText, Icon, theme, useNavbarContainerHeight } from '@sg/garnish';

import { useTrackEffect } from '@order/Telemetry';

export const FlexEntryPoint = (props: FlexEntryPointProps) => {
  const { onCheckEligibility, onDismiss } = props;
  const { formatMessage } = useIntl();
  const { offsetStyle } = useNavbarContainerHeight();

  useTrackEffect('home.hsafsa_campaign_banner.view');

  return (
    <View style={[styles.container, styles.absoluteWrapper, offsetStyle]}>
      <View style={styles.background} />
      <BlurView
        intensity={80}
        tint="light"
        style={[StyleSheet.absoluteFill, pointerEventsStyles.none]}
      />

      <TouchableOpacity
        role="link"
        aria-label={formatMessage(messages.check)}
        style={styles.content}
        onPress={onCheckEligibility}
      >
        <View style={styles.flexIcon}>
          <Icon name="IconFlex" color={theme.colors.WHITE} width={36} height={16} />
        </View>

        <View style={styles.info}>
          <BodyText sizeMatch={['14']} style={styles.title} bold>
            <FormattedMessage {...messages.title} />
          </BodyText>

          <BodyText sizeMatch={['12']} style={styles.description}>
            <FormattedMessage {...messages.description} />
          </BodyText>
        </View>
      </TouchableOpacity>

      <TouchableOpacity
        role="button"
        aria-label={formatMessage(messages.dismiss)}
        style={styles.closeIcon}
        onPress={onDismiss}
      >
        <Icon name="IconClose" color={theme.colors.GRAY} width={16} height={16} />
      </TouchableOpacity>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Pay using your HSA / FSA',
    description: 'Flex Entry Point | Title',
  },
  description: {
    defaultMessage: 'Check if you qualify here',
    description: 'Flex Entry Point | Description',
  },
  check: {
    defaultMessage: 'Check eligibility',
    description: 'Flex Entry Point | Check',
  },
  dismiss: {
    defaultMessage: 'Dismiss',
    description: 'Flex Entry Point | Dismiss',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  absoluteWrapper: {
    position: 'absolute',
    zIndex: 1,
    bottom: 0,
    left: theme.spacing['4'],
    right: theme.spacing['4'],
  },
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    overflow: 'hidden',
    gap: theme.spacing['2'],
    borderRadius: theme.radius['xxlarge'],
    paddingHorizontal: theme.spacing['6'],
    paddingVertical: theme.spacing['4'],
  },
  background: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  info: {
    flex: 1,
    gap: theme.spacing['1'],
  },
  flexIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: 'rgba(11, 0, 119, 1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeIcon: {
    width: 24,
    height: 24,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: theme.colors.GRAY,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    color: theme.colors.CHARCOAL,
  },
  description: {
    color: theme.colors.CHARCOAL,
    textDecorationLine: 'underline',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type FlexEntryPointProps = {
  onCheckEligibility: () => void;
  onDismiss: () => void;
};
